import { useEffect, useState } from 'react';

// More comprehensive mobile device detection
function detectMobileDevice(): boolean {
  // Check user agent for mobile patterns
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry',
    'windows phone', 'opera mini', 'iemobile', 'mobile safari'
  ];

  const isMobileUserAgent = mobileKeywords.some(keyword =>
    userAgent.includes(keyword)
  );

  // Check for touch capability (but this alone isn't reliable)
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Check for mobile-specific features
  const hasOrientationChange = 'orientation' in window;

  // Check screen characteristics typical of mobile devices
  const smallScreen = window.innerWidth <= 768 && window.innerHeight <= 1024;
  const highDPR = window.devicePixelRatio > 1.5;

  // Mobile devices typically have:
  // 1. Mobile user agent string
  // 2. Touch screen capability
  // 3. Orientation change support
  // 4. Small screen with high pixel density

  // Primary check: user agent (most reliable)
  if (isMobileUserAgent) {
    return true;
  }

  // Secondary check: combination of mobile characteristics
  // Only consider it mobile if multiple indicators are present
  const mobileIndicators = [
    hasTouchScreen,
    hasOrientationChange,
    smallScreen && highDPR
  ].filter(Boolean).length;

  // Require at least 2 mobile indicators AND small screen
  return mobileIndicators >= 2 && smallScreen;
}

export function useIsMobile() {
  const [ isMobile, setIsMobile ] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(detectMobileDevice());
    };

    // Check on mount
    checkMobile();

    // Listen for orientation changes (mobile-specific)
    window.addEventListener('orientationchange', checkMobile);

    // Also listen for resize, but with more sophisticated detection
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('orientationchange', checkMobile);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return !!isMobile;
}
